// src/services/adminEmailsService.js
// Admin Email Whitelist API service
// Manages whitelist of admin emails for auto-onboarding

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Helper — authenticated fetch wrapper
 */
const authFetch = async (url, idToken, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Request failed: ${res.status}`);
  }

  return res.json();
};

/**
 * Add one or more emails to admin whitelist
 * POST /api/admin/emails/whitelist
 * 
 * @param {Array} emails - Array of {email, role, fullName, reason, permissions}
 * @param {string} idToken - Firebase ID token
 * @returns {Promise} - {success, added, failed}
 */
export const addWhitelist = async (emails, idToken) => {
  return authFetch(`${API_URL}/api/admin/emails/whitelist`, idToken, {
    method: 'POST',
    body: JSON.stringify({ emails }),
  });
};

/**
 * List all whitelisted admin emails with pagination and filtering
 * GET /api/admin/emails/whitelist
 * 
 * @param {Object} params - {page, limit, status, role, search}
 * @param {string} idToken - Firebase ID token
 * @returns {Promise} - {success, data, pagination}
 */
export const listWhitelist = async (params = {}, idToken) => {
  const {
    page = 1,
    limit = 50,
    status = 'all',
    role = 'all',
    search = '',
  } = params;

  const query = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    status,
    role,
    search,
  });

  return authFetch(
    `${API_URL}/api/admin/emails/whitelist?${query}`,
    idToken,
    { method: 'GET' }
  );
};

/**
 * Get details of a single whitelisted email entry
 * GET /api/admin/emails/whitelist/:emailId
 * 
 * @param {string} emailId - MongoDB ID of the whitelist entry
 * @param {string} idToken - Firebase ID token
 * @returns {Promise} - {success, data}
 */
export const getWhitelist = async (emailId, idToken) => {
  return authFetch(
    `${API_URL}/api/admin/emails/whitelist/${emailId}`,
    idToken,
    { method: 'GET' }
  );
};

/**
 * Update a whitelist entry (role, permissions, reason)
 * PUT /api/admin/emails/whitelist/:emailId
 * 
 * @param {string} emailId - MongoDB ID of the whitelist entry
 * @param {Object} updates - {role, permissions, reason}
 * @param {string} idToken - Firebase ID token
 * @returns {Promise} - {success, data}
 */
export const updateWhitelist = async (emailId, updates, idToken) => {
  return authFetch(
    `${API_URL}/api/admin/emails/whitelist/${emailId}`,
    idToken,
    {
      method: 'PUT',
      body: JSON.stringify(updates),
    }
  );
};

/**
 * Remove email from whitelist (soft delete)
 * DELETE /api/admin/emails/whitelist/:emailId
 * 
 * @param {string} emailId - MongoDB ID of the whitelist entry
 * @param {Object} data - {reason} (optional)
 * @param {string} idToken - Firebase ID token
 * @returns {Promise} - {success, message}
 */
export const removeWhitelist = async (emailId, data = {}, idToken) => {
  return authFetch(
    `${API_URL}/api/admin/emails/whitelist/${emailId}`,
    idToken,
    {
      method: 'DELETE',
      body: JSON.stringify(data),
    }
  );
};

/**
 * Check if an email is whitelisted for admin access
 * GET /api/admin/emails/check/:email
 * 
 * PUBLIC endpoint - no auth required
 * Used during signup to determine auto-role
 * 
 * @param {string} email - Email address to check
 * @returns {Promise} - {whitelisted, role, permissions}
 */
export const checkEmail = async (email) => {
  try {
    const res = await fetch(
      `${API_URL}/api/admin/emails/check/${encodeURIComponent(email)}`
    );

    if (!res.ok) {
      // If check fails, default to not whitelisted (safe default)
      return { whitelisted: false };
    }

    return res.json();
  } catch (error) {
    console.error('Error checking email whitelist:', error);
    // Default to not whitelisted on error
    return { whitelisted: false };
  }
};

/**
 * Export as object for consistency with other services
 */
export const adminEmailsService = {
  addWhitelist,
  listWhitelist,
  getWhitelist,
  updateWhitelist,
  removeWhitelist,
  checkEmail,
};

export default adminEmailsService;
